import { acquireToken } from '../auth/msal.js'
import { getDeviceCreds } from '../auth/deviceStore.js'

/**
 * FlawDesk-API-Client für das Outlook Add-in.
 *
 * Wraps `fetch` mit:
 *  - Bearer-Token aus MSAL.js (NAA)
 *  - JSON-Default-Headers
 *  - Auth-bezogene Backend-Errors als typed Errors
 *
 * Das Backend (#1086 + #1087) signalisiert spezielle Zustände über
 * Status-Codes + JSON-Body mit `error`-Feld:
 *   401 invalid_token       → Token konnte nicht validiert werden
 *   403 tenant_not_allowed  → Tenant nicht in der Allowlist
 *   202 pending_approval    → User wartet auf Admin-Approval
 *
 * Wir mappen die auf JS-Errors mit `.code`, damit der Taskpane sie
 * deklarativ in UI-States übersetzen kann.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://api.flawdesk.de'

export class ApiError extends Error {
  /**
   * @param {string} code — z.B. 'tenant_not_allowed', 'pending_approval',
   *                        'invalid_token', 'http_error', 'network_error'
   * @param {string} message — menschlicher Fehlertext
   * @param {number=} status — HTTP-Status falls vorhanden
   * @param {object=} body — geparster Response-Body falls vorhanden
   */
  constructor(code, message, status, body) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.body = body
  }
}

/**
 * Holt ein Token, hängt es als Bearer an, parsed JSON, mappt
 * bekannte Fehler-Shapes auf ApiError mit `code`.
 *
 * @param {string} path  — z.B. '/me' oder '/tickets'
 * @param {RequestInit=} options
 * @returns Geparster JSON-Body. Wirft ApiError bei Fehlern.
 */
export async function apiFetch(path, options = {}) {
  let tokenResult
  try {
    tokenResult = await acquireToken()
  } catch (err) {
    throw new ApiError('auth_failed', err?.message || 'Auth failed')
  }

  const url = `${API_BASE}${path}`

  // #1537: E2E-Geraete-Header anhaengen, falls fuer diesen Account vorhanden.
  // Damit kann das Backend den Workspace-Key fuer diese passwortlose Entra-
  // Session entsperren (WorkspaceCrypto::keyFor -> getWorkspaceKeyViaDevice).
  // Best-effort: ohne Creds laeuft der Request normal weiter und das Backend
  // antwortet ggf. mit workspace_encryption_required/session_locked.
  let deviceHeaders = {}
  try {
    const accountKey = tokenResult.account?.homeAccountId
    if (accountKey) {
      const creds = await getDeviceCreds(accountKey)
      if (creds) {
        deviceHeaders = {
          'X-FD-Device-Id': creds.device_id,
          'X-FD-Device-Secret': creds.device_secret,
        }
      }
    }
  } catch {
    // ignore -- Geraete-Header sind optional
  }

  let res
  try {
    res = await fetch(url, {
      ...options,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...deviceHeaders,
        ...(options.headers || {}),
        Authorization: `Bearer ${tokenResult.accessToken}`,
      },
    })
  } catch (netErr) {
    throw new ApiError('network_error', netErr?.message || 'Network error')
  }

  // 204 No Content
  if (res.status === 204) return null

  let body = null
  try {
    body = await res.json()
  } catch {
    // Backend hat kein JSON geliefert. Lassen wir als null durch.
  }

  if (res.ok) {
    return body
  }

  // Spezial-Mapping: Backend signalisiert Auth-Zustände ueber zwei Felder:
  //   error      — Coarse-grained category, z.B. 'invalid_token', 'tenant_not_allowed'
  //   error_code — Feinere Variante, z.B. 'expired', 'wrong_audience', 'unknown_kid'
  //   hint       — Menschenlesbare Empfehlung
  // Wir priorisieren error_code (granular) und fallen auf error zurueck.
  const code = body?.error_code || body?.error || `http_${res.status}`
  const message = body?.hint || body?.message || body?.error || `Request failed (${res.status})`

  throw new ApiError(code, message, res.status, body)
}
